<template>
	<Accordion v-if="annualReviewDetails" label="Courtesy Re-Pull Credit Report Information" backgroundColor="#e4e5ff">
		<div class="grid">
			<div class="col-4 stack">
				<DetailsCard
					:cardTitle="'Courtesy Re-pull Details'"
					:referenceId="referenceId"
					:applicationUrl="applicationUrl"
					:details="annualReviewDetails"
					:creditPullTypeId="CreditPullTypes.COURTESY_REPULL"
					@reloadAnnualReviewAsync="onReloadAnnualReviewAsync" />
				<PermissionsWrapper
					:permissionIds="[Permissions.AnnualReview]"
					:permissionLevel="PermissionLevels.Update">
					<button v-if="shouldShowRedecisionButton" @click="openRedecisionModal">Re-decision Error</button>
					<button
						v-if="annualReviewDetails.annualReviewStatusId === AnnualReviewStatusTypes.DECISION_ERROR"
						@click="openRedecisionModal">
						Re-decision Error
					</button>
					<button
						v-else-if="annualReviewDetails.annualReviewStatusId === AnnualReviewStatusTypes.DECLINED"
						@click="openRepullModal">
						Courtesy Re-pull
					</button>
				</PermissionsWrapper>
			</div>
			<CourtesyRepullDatesCard
				class="col-3"
				:borrowerPullDate="annualReviewDetails.borrowerCourtesyRepullDateFormatted"
				:coBorrowerPullDate="annualReviewDetails.coBorrowerCourtesyRepullDateFormatted"
				:finalStatusDate="annualReviewDetails.finalStatusDateFormatted" />
			<CreditReportInformation
				class="col-12"
				:loanId="loanId"
				:annualReviewId="selectedAnnualReviewId"
				:lenderId="lenderId"
				:programTypeId="programTypeId"
				:productTypeId="productTypeId"
				:applicationTypeId="annualReviewDetails.applicationTypeId"
				:creditPullTypeId="CreditPullTypes.COURTESY_REPULL" />
		</div>

		<RedecisionErrorModal
			v-if="viewingRedecision"
			:annualReviewId="annualReviewDetails.id"
			:annualReviewYear="annualReviewDetails.annualReviewYear"
			:loanId="loanId"
			@close="closeRedecisionModal" />

		<CourtesyRepullModal
			v-if="viewingRepull"
			:annualReviewId="annualReviewDetails.id"
			:annualReviewYear="annualReviewDetails.annualReviewYear"
			:loanId="loanId"
			@close="closeRepullModal" />
	</Accordion>
</template>

<script setup lang="ts">
	import { ref, computed } from 'vue';
	import { AnnualReviewDetails } from '@/models/loans';
	import { CreditPullTypes, AnnualReviewStatusTypes } from '@/types/annual-review';
	import Accordion from '@/components/shared/Accordion.vue';
	import DetailsCard from '@/components/loan/annual-review/DetailsCard.vue';
	import CourtesyRepullDatesCard from '@/components/loan/annual-review/CourtesyRepullDatesCard.vue';
	import CreditReportInformation from '@/components/loan/annual-review/CreditReportInformation.vue';
	import CourtesyRepullModal from '@/components/loan/annual-review/CourtesyRepullModal.vue';
	import RedecisionErrorModal from '@/components/loan/annual-review/RedecisionErrorModal.vue';
	import PermissionsWrapper from '@/components/wrappers/PermissionsWrapper.vue';
	import { Permissions, PermissionLevels } from '@/models/permissions';

	const props = defineProps<{
		loanId: number;
		lenderId: number;
		productTypeId: number;
		programTypeId: number;
		referenceId: string;
		applicationUrl: string;
		annualReviewDetails: AnnualReviewDetails;
		selectedAnnualReviewId: number;
		onReloadAnnualReviewAsync: () => Promise<void>;
	}>();

	const redecisionStatuses = [AnnualReviewStatusTypes.CREDIT_ALERT, AnnualReviewStatusTypes.DECISION_ERROR];

	const viewingRepull = ref(false);
	const viewingRedecision = ref(false);

	const closeRepullModal = () => (viewingRepull.value = false);
	const openRepullModal = () => (viewingRepull.value = true);

	const closeRedecisionModal = () => (viewingRedecision.value = false);
	const openRedecisionModal = () => (viewingRedecision.value = true);

	const shouldShowRedecisionButton = computed(() =>
		redecisionStatuses.includes(props.annualReviewDetails.annualReviewStatusId)
	);
</script>

<style scoped src="@/assets/css/loans/form.css" />
<style scoped src="@/assets/css/grid.css" />

<style scoped>
	.form-container {
		margin-bottom: 1rem;
	}

	select {
		height: fit-content;
		padding: 8px;
	}

	button {
		width: 100%;
		max-height: 38px;
		padding: 8px;
		background-color: black;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 13px;
		font-family: 'Montserrat', arial, sans-serif;
		font-weight: bold;
		transition: background-color 0.2s, color 0.2s, opacity 0.2s;
	}

	button:hover {
		background-color: #333;
	}

	button:disabled {
		background-color: #ddd;
		color: #666;
		cursor: not-allowed;
		opacity: 0.7;
	}
</style>